import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LeadDetail } from './LeadDetail';

interface Lead {
  id: string;
  name: string;
  email?: string;
  stage: string;
  source?: {
    keyword?: string;
    channel?: string;
    project?: string;
  };
  fit?: {
    scoreBand?: string;
  };
  nextFollowUpAt?: string;
  lastTouchAt?: string;
}

export const LeadsDashboard: React.FC<{ filterProject?: string }> = ({ filterProject }) => {
  const { getIdToken } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  
  // Filter state
  const [activeFilter, setActiveFilter] = useState<{ type: 'channel' | 'keyword', value: string } | null>(null);

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const token = await getIdToken();
        if (!token) return;
        
        const res = await fetch('/api/leads', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        
        let data = await res.json();
        
        if (filterProject) {
          if (filterProject === 'dabbler') {
            data = data.filter((l: Lead) => !l.source?.project || l.source.project === 'dabbler');
          } else {
            data = data.filter((l: Lead) => l.source?.project === filterProject);
          }
        }
        
        setLeads(data);
      } catch (error) {
        console.error('Failed to fetch leads', error);
      } finally {
        setLoading(false);
      }
    };
    if (!selectedLeadId) fetchLeads();
  }, [getIdToken, selectedLeadId]);

  if (selectedLeadId) {
    return <LeadDetail leadId={selectedLeadId} onBack={() => setSelectedLeadId(null)} />;
  }

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>Loading leads from Dabbler...</div>;

  // Calculate summaries
  const channelCounts: Record<string, number> = {};
  const keywordCounts: Record<string, number> = {};
  
  leads.forEach(l => {
    const ch = l.source?.channel || 'unknown';
    const kw = l.source?.keyword || 'none';
    channelCounts[ch] = (channelCounts[ch] || 0) + 1;
    keywordCounts[kw] = (keywordCounts[kw] || 0) + 1;
  });

  // Apply filters
  const displayedLeads = leads.filter(l => {
    if (!activeFilter) return true;
    if (activeFilter.type === 'channel') return (l.source?.channel || 'unknown') === activeFilter.value;
    if (activeFilter.type === 'keyword') return (l.source?.keyword || 'none') === activeFilter.value;
    return true;
  });

  const toggleFilter = (type: 'channel' | 'keyword', value: string) => {
    if (activeFilter?.type === type && activeFilter.value === value) {
      setActiveFilter(null); // toggle off
    } else {
      setActiveFilter({ type, value });
    }
  };

  return (
    <div style={{ padding: '2rem', background: '#f8fafc', minHeight: 'calc(100vh - 100px)' }}>
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 800, color: '#0f172a', margin: '0 0 0.5rem 0' }}>Leads Dashboard</h1>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#475569' }}>
            Review and qualify your keyword responders across all channels.
          </p>
        </div>
        {activeFilter && (
          <button 
            onClick={() => setActiveFilter(null)}
            style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '0.5rem 1rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, color: '#475569', cursor: 'pointer' }}
          >
            Clear Filter ✕
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: '2rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        {/* Channels */}
        <div style={{ flex: 1, minWidth: '300px' }}>
          <h3 style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>By Channel</h3>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {Object.entries(channelCounts).map(([ch, count]) => {
              const isActive = activeFilter?.type === 'channel' && activeFilter.value === ch;
              return (
                <div 
                  key={ch}
                  onClick={() => toggleFilter('channel', ch)}
                  style={{ 
                    background: isActive ? '#eff6ff' : 'white', 
                    border: isActive ? '2px solid #3b82f6' : '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '0.75rem 1rem', 
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    minWidth: '120px'
                  }}
                >
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: isActive ? '#1d4ed8' : '#0f172a' }}>{count}</div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: isActive ? '#1e40af' : '#64748b', textTransform: 'capitalize' }}>{ch}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Keywords */}
        <div style={{ flex: 1, minWidth: '300px' }}>
          <h3 style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.75rem' }}>By Keyword</h3>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {Object.entries(keywordCounts).map(([kw, count]) => {
              const isActive = activeFilter?.type === 'keyword' && activeFilter.value === kw;
              return (
                <div 
                  key={kw}
                  onClick={() => toggleFilter('keyword', kw)}
                  style={{ 
                    background: isActive ? '#f0fdf4' : 'white', 
                    border: isActive ? '2px solid #22c55e' : '1px solid #e2e8f0', 
                    borderRadius: '8px', 
                    padding: '0.75rem 1rem', 
                    cursor: 'pointer',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    minWidth: '120px'
                  }}
                >
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: isActive ? '#15803d' : '#0f172a' }}>{count}</div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 600, color: isActive ? '#166534' : '#64748b', textTransform: 'uppercase' }}>{kw}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead style={{ background: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
            <tr>
              <th style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</th>
              <th style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stage</th>
              <th style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Channel</th>
              <th style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Keyword</th>
              <th style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Fit Score</th>
              <th style={{ padding: '1rem 1.5rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Next Follow-up</th>
            </tr>
          </thead>
          <tbody>
            {displayedLeads.map((lead, idx) => (
              <tr 
                key={lead.id} 
                style={{ 
                  cursor: 'pointer', 
                  background: idx % 2 === 0 ? 'white' : '#fafafa',
                  borderBottom: '1px solid #f1f5f9',
                  transition: 'background 0.2s'
                }}
                onClick={() => setSelectedLeadId(lead.id)}
                onMouseEnter={(e) => e.currentTarget.style.background = '#eff6ff'}
                onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? 'white' : '#fafafa'}
              >
                <td style={{ padding: '1.25rem 1.5rem' }}>
                  <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.875rem' }}>{lead.name || 'Unknown'}</div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>{lead.email}</div>
                </td>
                <td style={{ padding: '1.25rem 1.5rem' }}>
                  <span style={{ 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '4px', 
                    background: '#dbeafe', 
                    color: '#1e40af', 
                    fontSize: '0.65rem', 
                    fontWeight: 700, 
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    border: '1px solid #bfdbfe'
                  }}>
                    {lead.stage?.replace('_', ' ')}
                  </span>
                </td>
                <td style={{ padding: '1.25rem 1.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#475569', textTransform: 'capitalize' }}>
                  {lead.source?.channel || 'unknown'}
                </td>
                <td style={{ padding: '1.25rem 1.5rem', fontSize: '0.875rem', fontWeight: 700, color: '#334155', textTransform: 'uppercase' }}>
                  {lead.source?.keyword || '-'}
                </td>
                <td style={{ padding: '1.25rem 1.5rem', fontSize: '0.875rem', color: '#475569' }}>
                  {lead.fit?.scoreBand || 'Pending'}
                </td>
                <td style={{ padding: '1.25rem 1.5rem', fontSize: '0.875rem', color: '#475569', fontWeight: 500 }}>
                  {lead.nextFollowUpAt ? new Date(lead.nextFollowUpAt).toLocaleDateString() : 'Not set'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {displayedLeads.length === 0 && (
          <div style={{ padding: '4rem', textAlign: 'center', color: '#64748b', background: 'white' }}>
            No leads found for this filter.
          </div>
        )}
      </div>
    </div>
  );
};